/**
 * 最後のフレームのダイアログ (docs/features/scene-frame-generation-tuning-plan.md §4.4・§4.5)。
 *
 * 「最初のフレーム → 最後のフレーム」を同じ比率・同じ寸法で 2 枚並べ、矢印でつなぐ。
 * - 左 = シーンの現在の最初のフレーム。無ければ点線枠で案内し、右の生成ボタンは押せない (現行どおり)
 * - 右 = 最後のフレーム。無ければ点線枠 (素材をドロップ / 生成 + モデル選択)。設定済みなら画像の上に
 *   再生成 (下中央) と解除の × (右上・常時表示)。別立ての「解除」ボタンは × と同じ処理だったので撤去 (2026-10-29利用者指示)
 * - 素材パネルは既定で閉じたまま。「最後のフレーム」ラベル行の右端の開閉アイコン (フローティングボタンと同じ) か、
 *   空の枠のクリックで開く
 * - 次のシーンが場面継続のときは右側を薄くして注記する (生成はできる)
 * - 外枠は素材パネルの下の段 (modal-overlay--beside-panel)。パネルからのドロップは幕越しに受け取れる
 * - 寸法 (2026-10-29利用者指示): スクロールさせない。カードの高さは画面の高さに連動 (上限あり)・幅は画面高から逆算した
 *   推定値で横長比率ほど広くなる (ScenarioGenerator.module.css の .lastFrameDialogCard)。2 枚のフレームはフレーム行の
 *   残り領域を ResizeObserver で測り、lastFrameDialogLayout.ts の計算で同じ寸法 (px) に決め、カードの幅は 2 枚に
 *   フィットさせる (推定幅との差で生じる左右の余白を無くす)
 * - 閉じ方 (2026-10-29利用者指示): フッターの「適用」= 今の状態のまま閉じる。右上の × / Escape = 開いたときの状態
 *   (snapshot) に戻して閉じる (生成・ドロップ・解除は即時に保存されるため、× は「開いた時点へ戻す」操作になる)。
 *   生成中は戻せないので、× は閉じるだけ (結果はそのまま反映される)。× のツールチップは「キャンセル」。
 *   外側クリックでは閉じない (誤って戻さないため)
 * - 開閉は呼び出し元の単一 state。生成後・読込時の自動展開はしない (ボタンの色で設定済みが分かる)
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../../../hooks/useToast';
import { brandConfig } from '../../../../config/brands';
import { useMaterialsPanelStore } from '../../../../stores/materialsPanelStore';
import { Tooltip } from '../../../common/Tooltip';
import styles from '../ScenarioGenerator.module.css';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useSceneStateContext } from '../hooks/SceneStateContext';
import { useMaterialLookup } from './useMaterialLookup';
import { computeLastFrameDialogCardWidth, computeLastFrameDialogFrameSize, parseAspectRatioNumber, type LastFrameDialogFrameSize } from './lastFrameDialogLayout';
import type { Scenario } from '../types';
import '../../../../styles/modal-common.css';
import '../../../../styles/buttons.css';


interface SceneLastFrameDialogProps {
  scenario: Scenario;
  scene: Scenario['scenes'][number];
  sceneKey: string;
  /** 次のシーンが場面継続 (このシーンの最後のフレームは使われない) */
  isContinuedByNext: boolean;
  onClose: () => void;
}

type LastFrameSnapshot = { url: string; sourceUrl?: string; materialId: string; width?: number; height?: number } | null;

/** 開いたときの状態と今の状態が同じか (素材 ID か表示 URL が違えば変更あり) */
function isSameLastFrame(a: LastFrameSnapshot, b: LastFrameSnapshot): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.materialId === b.materialId && a.url === b.url;
}

export function SceneLastFrameDialog({ scenario, scene, sceneKey: key, isContinuedByNext, onClose }: SceneLastFrameDialogProps) {
  const {
    videoSettings,
    isGlobalDragging,
    isDraggingVideo,
    dragOverLastFrameKey,
    setDragOverLastFrameKey,
    creditBadgeText,
    handleGenerateLastFrame,
    handleDropLastFrameMaterial,
    handleRemoveLastFrame,
    handleRestoreLastFrame,
    openFrameDropdown,
    openMaterialsPanel,
    setPreviewModalMaterial,
  } = useSceneHandlers();
  const { scenePreviews, scenePreviewIndex, scenePreviewMaterials, sceneLastFrames, lastFrameLoading } = useSceneStateContext();
  const { resolveMaterial } = useMaterialLookup();
  const toast = useToast();
  const isMaterialsPanelOpen = useMaterialsPanelStore((state) => state.isOpen);
  const closeMaterialsPanel = useMaterialsPanelStore((state) => state.close);

  const firstFrameUrl = (scenePreviews[key] || [])[scenePreviewIndex[key] || 0] || null;
  const hasFirstFrame = !!(scenePreviewMaterials[key]?.length) && !!firstFrameUrl;
  const lastFrame = sceneLastFrames[key] ?? null;
  const loading = !!lastFrameLoading[key];
  const sceneNumber = (scenario.scenes || []).filter((s) => !s.brand_cut_position).findIndex((s) => s.id === scene.id) + 1;
  const title = sceneNumber > 0 ? `シーン${sceneNumber}の最後のフレーム` : '最後のフレーム';

  // 開いたときの状態。× / Escape はここへ戻す (ダイアログは開くたびにマウントされる)
  const snapshotRef = useRef<LastFrameSnapshot>(lastFrame);
  const dirty = !isSameLastFrame(snapshotRef.current, lastFrame);

  /** × / Escape: 開いたときの状態に戻して閉じる。生成中は戻せないので閉じるだけ */
  const handleRevertAndClose = () => {
    if (!loading && dirty) {
      void handleRestoreLastFrame(scenario, scene, snapshotRef.current);
    }
    onClose();
  };
  // ツールチップは「キャンセル」固定 (2026-10-29利用者指示)。挙動は上記のとおり開いたときの状態に戻す
  const revertLabel = 'キャンセル';

  // Escape で閉じる (× と同じ)。素材パネルが開いている間はパネル側の Escape に譲る (二重に閉じない)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isMaterialsPanelOpen) {
        e.preventDefault();
        handleRevertAndClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, isMaterialsPanelOpen, loading, dirty]);

  const openLastFramePreview = async () => {
    if (!lastFrame?.materialId) return;
    const material = await resolveMaterial(lastFrame.materialId);
    if (material) setPreviewModalMaterial(material);
  };

  const openFirstFramePreview = async () => {
    const material = scenePreviewMaterials[key]?.[scenePreviewIndex[key] || 0];
    if (material) setPreviewModalMaterial(material);
  };

  const generateButton = (label: string, dropdownId: string, small: boolean) => (
    <div className={`${styles.frameGenerateGroup} ${small ? styles.frameGenerateGroupSmall : ''} ${loading ? 'ai-loading-active' : ''}`}>
      <button
        type="button"
        className={styles.frameGenerateButton}
        onClick={(e) => { e.stopPropagation(); handleGenerateLastFrame(scenario, scene, key); }}
        disabled={loading || !hasFirstFrame}
        data-tooltip={hasFirstFrame ? `${creditBadgeText}ポイントを消費します` : '最初のフレームを先に生成してください'}
        data-tooltip-delay=""
      >
        {loading ? (
          <>
            生成中
            <span className="ai-loading-dots"><span>.</span><span>.</span><span>.</span></span>
          </>
        ) : (
          <>
            <span>{label}</span>
            <span className={styles.creditBadge}>{creditBadgeText}</span>
          </>
        )}
      </button>
      <div className={styles.frameModelDropdown}>
        <button
          type="button"
          className={styles.frameModelDropdownTrigger}
          onClick={(e) => { e.stopPropagation(); openFrameDropdown(dropdownId, e.currentTarget); }}
          disabled={loading}
        >
          <span className={styles.frameModelArrow}>▼</span>
        </button>
      </div>
    </div>
  );

  const panelToggleLabel = isMaterialsPanelOpen ? `${brandConfig.labels.material}パネルを閉じる` : `${brandConfig.labels.material}パネルを開く`;

  // フレームの比率 (幅 / 高さ)。カードの高さと初期幅は CSS (--lf-ar)、フレームの寸法とカードの最終的な幅は下の ResizeObserver で決める
  const aspect = parseAspectRatioNumber(videoSettings.aspectRatio);

  // フレーム行の残り領域から 2 枚のフレームの寸法を決める (測るまでは非表示)。
  // 行の寸法はカード (画面高・素材パネル幅に連動) で決まり、中身には依存しない (flex: 1 1 0; min-height: 0)。
  // カードの幅は 2 枚にフィットさせる (CSS の推定幅で生じる左右の余白を無くす。縦並びでは CSS の幅いっぱい)。
  // フィット幅にしても行の幅から同じフレーム幅が出るので、ResizeObserver が往復することはない
  const framesRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<{ frame: LastFrameDialogFrameSize; cardWidth: number | null } | null>(null);
  useLayoutEffect(() => {
    const el = framesRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      const stacked = getComputedStyle(el).flexDirection === 'column';
      const frame = computeLastFrameDialogFrameSize(el.clientWidth, el.clientHeight, aspect, stacked);
      const cardWidth = computeLastFrameDialogCardWidth(frame.width, stacked);
      setLayout((prev) => (
        prev && prev.frame.width === frame.width && prev.frame.height === frame.height && prev.cardWidth === cardWidth
          ? prev
          : { frame, cardWidth }
      ));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [aspect]);
  const cardStyle = {
    '--lf-ar': aspect,
    ...(layout?.cardWidth != null ? { width: layout.cardWidth } : {}),
  } as CSSProperties;
  const frameStyle: CSSProperties = layout
    ? { width: layout.frame.width, height: layout.frame.height }
    : { visibility: 'hidden' };

  return createPortal(
    <div className="modal-overlay modal-overlay--beside-panel">
      <div
        className={`modal-card ${styles.lastFrameDialogCard} ${isContinuedByNext ? styles.lastFrameDialogCardWithNote : ''}`}
        style={cardStyle}
        onClick={(e) => e.stopPropagation()}
        data-testid="scene-last-frame-dialog"
      >
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <Tooltip content={revertLabel} placement="bottom">
            <button type="button" className="modal-close-button" onClick={handleRevertAndClose} aria-label={revertLabel}>×</button>
          </Tooltip>
        </div>
        <div className={`modal-body ${styles.lastFrameDialogBody}`}>
          <div className={styles.lastFrameDialogFrames} ref={framesRef}>
            {/* 左: 最初のフレーム */}
            <div className={styles.lastFrameDialogColumn}>
              <div className={styles.lastFrameDialogLabelRow}>
                <span className={styles.lastFrameDialogLabel}>最初のフレーム</span>
              </div>
              <div className={`${styles.lastFrameWrapper} ${styles.lastFrameDialogFrame}`} style={frameStyle}>
                {hasFirstFrame ? (
                  <div className={styles.lastFrameImageContainer} onClick={() => { void openFirstFramePreview(); }} style={{ cursor: 'pointer' }}>
                    <img src={firstFrameUrl as string} alt={`シーン${scene.order}の最初のフレーム`} />
                  </div>
                ) : (
                  <div className={styles.lastFrameDialogEmptyFirst}>最初のフレームを先に生成してください</div>
                )}
              </div>
            </div>

            <span className={styles.lastFrameDialogArrow} aria-hidden="true" />

            {/* 右: 最後のフレーム */}
            <div className={styles.lastFrameDialogColumn} style={{ opacity: isContinuedByNext ? 0.55 : 1 }}>
              <div className={styles.lastFrameDialogLabelRow}>
                <span className={styles.lastFrameDialogLabel}>最後のフレーム</span>
                {/* 素材パネルの開閉 (ラベル行の右端) */}
                <Tooltip content={panelToggleLabel} placement="top">
                  <button
                    type="button"
                    className={styles.lastFramePanelToggle}
                    onClick={(e) => { e.stopPropagation(); if (isMaterialsPanelOpen) closeMaterialsPanel(); else openMaterialsPanel('manage'); }}
                    aria-label={panelToggleLabel}
                    aria-pressed={isMaterialsPanelOpen}
                  >
                    <img src={isMaterialsPanelOpen ? '/layout-sidebar-right-collapse.svg' : '/layout-sidebar-right-expand.svg'} alt="" />
                  </button>
                </Tooltip>
              </div>
              <div
                className={`${styles.lastFrameWrapper} ${styles.lastFrameDialogFrame} ${dragOverLastFrameKey === key ? styles.dragOver : ''}`}
                style={frameStyle}
                onDragOver={(e) => {
                  if (isDraggingVideo) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                }}
                onDragEnter={(e) => {
                  if (isDraggingVideo) return;
                  e.preventDefault();
                  setDragOverLastFrameKey(key);
                }}
                onDragLeave={(e) => {
                  if (isDraggingVideo) return;
                  e.preventDefault();
                  if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragOverLastFrameKey(null);
                  }
                }}
                onDrop={(e) => {
                  if (isDraggingVideo) return;
                  e.preventDefault();
                  setDragOverLastFrameKey(null);
                  try {
                    const dataString = e.dataTransfer.getData('application/json');
                    if (!dataString) {
                      toast.error('ドロップされたデータが無効です');
                      return;
                    }
                    const materialData = JSON.parse(dataString);
                    if (materialData.contentType && !materialData.contentType.startsWith('image/')) {
                      toast.error('画像素材のみドロップできます');
                      return;
                    }
                    void handleDropLastFrameMaterial(scenario, scene, materialData);
                  } catch (error) {
                    console.error('ドロップ処理エラー:', error);
                    toast.error('素材のドロップに失敗しました');
                  }
                }}
              >
                {isGlobalDragging && !isDraggingVideo ? (
                  <div className={styles.dropPlaceholderSmall}>
                    <span>ドロップ</span>
                  </div>
                ) : lastFrame ? (
                  <div className={styles.lastFrameImageContainer} onClick={() => { void openLastFramePreview(); }} style={{ cursor: 'pointer' }}>
                    <img
                      src={lastFrame.url}
                      alt={`シーン${scene.order}の最後のフレーム`}
                      draggable={!!lastFrame.materialId}
                      onDragStart={(e) => {
                        if (!lastFrame.materialId) {
                          e.preventDefault();
                          return;
                        }
                        // ラストフレームは素材IDしか持たないので商品紐づきは載せない。受け側
                        // (SelectedAssetsPanel の ensureProductLinkFlag) が 1 件取得で asin / hasProductLinks を補う
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          id: lastFrame.materialId,
                          sourceUrl: lastFrame.sourceUrl || lastFrame.url,
                          thumbnailUrl: lastFrame.url,
                          contentType: 'image/png',
                        }));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                    />
                    {/* 解除 (右上・常時表示) */}
                    <button
                      type="button"
                      className={styles.lastFrameCloseButton}
                      onClick={(e) => { e.stopPropagation(); void handleRemoveLastFrame(scenario, scene); }}
                      disabled={loading}
                      title="最後のフレームの設定を解除"
                      aria-label="最後のフレームの設定を解除"
                    >
                      ×
                    </button>
                    {/* 再生成 (画像の下中央に重ねる) */}
                    <div className={styles.lastFrameRegenerateOverlay} onClick={(e) => e.stopPropagation()}>
                      {generateButton('フレーム画像を再生成', `last-regenerate-${key}`, true)}
                    </div>
                  </div>
                ) : (
                  <div className={styles.lastFramePlaceholder} onClick={() => { if (!isMaterialsPanelOpen) openMaterialsPanel('manage'); }}>
                    {generateButton('最後のフレームを生成', `last-generate-${key}`, false)}
                    <div className={styles.placeholderDividerSmall}><span>または</span></div>
                    <span style={{ cursor: 'pointer' }} onClick={() => openMaterialsPanel('manage')}>
                      <span className={styles.desktopOnly} style={{ color: 'rgba(245, 241, 230, 0.35)', fontSize: '11px' }}>{brandConfig.labels.material}をドラッグ＆ドロップ</span>
                      <span className={styles.mobileOnly} style={{ color: 'rgba(245, 241, 230, 0.35)', fontSize: '11px' }}>{brandConfig.labels.material}を選択</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {isContinuedByNext && (
            <div className={styles.lastFrameDialogNote}>
              次のシーンが場面継続のため、このシーンの最後のフレームは使われません
            </div>
          )}
          <div className={styles.lastFrameDialogNote} style={{ opacity: 0.85 }}>
            最後のフレームは最初のフレームを基準に生成します。
          </div>
        </div>
        <div className={`modal-actions ${styles.lastFrameDialogActions}`}>
          <button type="button" className="btn-modal-sub" onClick={onClose}>適用</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
